// Public self-serve signup (PRD §3.3 step 1). Creates the Firebase Auth user,
// sets their display name, then provisions the tenant in the SAME step so a new
// customer lands on the dashboard rather than a second onboarding form.
//
// If provisionTenant fails after the account is created, we do NOT treat it as a
// fatal error: the account exists and TenantGate will show its own onboarding
// form on the next render. Blocking here would strand the user with an account
// they cannot use.
import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  createUserWithEmailAndPassword,
  signInWithPopup,
  updateProfile,
} from 'firebase/auth';
import { auth, googleProvider } from '../lib/firebase';
import { useI18n } from '../lib/i18n';
import { provisionTenant } from '../lib/tenant';

const MIN_PASSWORD = 8;

export default function Signup() {
  const { t, lang, setLang } = useI18n();
  const navigate = useNavigate();
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function mapAuthError(err: unknown): string {
    const code = (err as { code?: string }).code ?? '';
    if (code.includes('email-already-in-use')) return t('signup.error.inUse');
    if (code.includes('invalid-email')) return t('signup.error.email');
    if (code.includes('weak-password')) return t('signup.error.weak');
    if (code.includes('operation-not-allowed')) return t('signup.error.disabled');
    return t('signup.error.generic');
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const name = businessName.trim();
    if (password.length < MIN_PASSWORD) {
      setError(t('signup.error.weak'));
      return;
    }
    setBusy(true);
    try {
      const cred = await createUserWithEmailAndPassword(auth, email.trim(), password);
      // Display name is a nicety; workspace provisioning is not. If it fails
      // the user still gets in (TenantGate re-prompts), but the failure must
      // be visible — a silent swallow here was indistinguishable from success.
      await updateProfile(cred.user, { displayName: name }).catch(() => {});
      await provisionTenant(name).catch((e) => {
        console.error('[signup] provisionTenant failed', e);
        sessionStorage.setItem('watag.pendingWorkspaceName', name);
      });
      navigate('/');
    } catch (err: unknown) {
      setError(mapAuthError(err));
      setBusy(false);
    }
  }

  async function onGoogle() {
    setError(null);
    setBusy(true);
    try {
      const cred = await signInWithPopup(auth, googleProvider);
      // Google gives us no business name, so fall back to their display name and
      // let them rename the workspace later in Settings.
      const fallback = businessName.trim() || cred.user.displayName || cred.user.email || 'My business';
      await provisionTenant(fallback).catch((e) => {
        console.error('[signup] provisionTenant failed', e);
        sessionStorage.setItem('watag.pendingWorkspaceName', fallback);
      });
      navigate('/');
    } catch (err: unknown) {
      setError(mapAuthError(err));
      setBusy(false);
    }
  }

  return (
    <div
      className="relative flex min-h-screen items-center justify-center overflow-hidden p-4"
      style={{
        background:
          'radial-gradient(600px 320px at 82% 8%, rgba(34,197,94,.12), transparent 60%), radial-gradient(700px 360px at 10% 30%, rgba(30,80,160,.18), transparent 60%), #0a1628',
      }}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'linear-gradient(rgba(120,160,210,.10) 1px, transparent 1px), linear-gradient(90deg, rgba(120,160,210,.10) 1px, transparent 1px)',
          backgroundSize: '44px 44px',
          maskImage: 'radial-gradient(circle at 60% 20%, #000, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(circle at 60% 20%, #000, transparent 75%)',
        }}
        aria-hidden
      />
      <div className="relative w-full max-w-md">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/watag-mark.svg" alt="WaTag" className="h-10 w-10" />
            <div>
              {/* Dark canvas, not a card — see the note in Login.tsx. */}
              <div className="font-extrabold text-white">{t('app.name')}</div>
              <div className="text-xs text-white/70">{t('app.tagline')}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setLang(lang === 'en' ? 'ar' : 'en')}
            className="rounded-full bg-white/10 px-3 py-1.5 text-sm font-bold text-white hover:bg-white/20"
          >
            {t('lang.switch')}
          </button>
        </div>

        <div className="rounded-card border border-slate-200 bg-white p-7 shadow-2xl">
          <h1 className="mb-1 text-xl font-extrabold text-ink">{t('signup.title')}</h1>
          <p className="mb-6 text-sm text-ink-3">{t('signup.subtitle')}</p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label htmlFor="businessName" className="mb-1 block text-sm font-semibold text-ink-2">
                {t('signup.business')}
              </label>
              <input
                id="businessName"
                type="text"
                required
                autoFocus
                maxLength={120}
                autoComplete="organization"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary-50"
              />
            </div>
            <div>
              <label htmlFor="email" className="mb-1 block text-sm font-semibold text-ink-2">
                {t('signup.email')}
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary-50"
              />
            </div>
            <div>
              <label htmlFor="password" className="mb-1 block text-sm font-semibold text-ink-2">
                {t('signup.password')}
              </label>
              <input
                id="password"
                type="password"
                required
                minLength={MIN_PASSWORD}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary-50"
              />
              <p className="mt-1 text-xs text-ink-3">{t('signup.passwordHint')}</p>
            </div>

            {error && (
              <p role="alert" className="text-sm font-semibold text-red-600">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={busy || !businessName.trim()}
              className="w-full rounded-lg bg-primary py-2.5 font-bold text-white hover:bg-primary-600 disabled:opacity-60"
            >
              {busy ? t('signup.loading') : t('signup.submit')}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3">
            <span className="h-px flex-1 bg-slate-200" />
            <span className="text-xs font-semibold text-ink-3">{t('login.or')}</span>
            <span className="h-px flex-1 bg-slate-200" />
          </div>

          <button
            type="button"
            onClick={onGoogle}
            disabled={busy}
            className="w-full rounded-lg border border-slate-300 py-2.5 font-bold text-ink hover:bg-surface disabled:opacity-60"
          >
            {t('login.google')}
          </button>

          <p className="mt-6 text-center text-sm text-ink-3">
            {t('signup.haveAccount')}{' '}
            <Link to="/login" className="font-bold text-primary hover:underline">
              {t('signup.signIn')}
            </Link>
          </p>
        </div>

        <p className="mt-5 px-2 text-center text-[11px] leading-relaxed text-white/70">
          {t('signup.terms')}
        </p>
        {/* Static files in admin/public — see the same note in Login.tsx. */}
        <p className="mt-2 flex flex-wrap justify-center gap-x-3 gap-y-1 text-[11px] font-semibold">
          <a href="/privacy.html" className="text-accent hover:underline">
            {t('login.privacy')}
          </a>
          <span className="text-white/50" aria-hidden>
            ·
          </span>
          <a href="/terms.html" className="text-accent hover:underline">
            {t('login.terms')}
          </a>
        </p>
      </div>
    </div>
  );
}
